import {
  type GraphPathLookupHostToDefaultGWParameters,
  type GraphPathLookupMulticastParameters,
  type GraphPathLookupParameters,
  type GraphPathLookupUnicastParameters,
  type PathLookupType,
  pathLookupHostToDefaultGWParametersSchema,
  pathLookupMulticastParametersSchema,
  pathLookupUnicastParametersSchema,
} from '@ipfabric/utils';
import {
  PATHLOOKUP_ADVANCED_OPTIONS_PRESET_ID_LOCAL_STORAGE,
  PATHLOOKUP_HOST_TO_DEFAULT_GW_TYPE_LOCAL_STORAGE,
  PATHLOOKUP_MULTICAST_TYPE_LOCAL_STORAGE,
  PATHLOOKUP_UNICAST_TYPE_LOCAL_STORAGE,
} from 'components/Graph/constants';
import type Joi from 'joi';
import { makeIsValid } from 'utils/joi/makeIsValid';

interface PathLookupLocalStorage {
  [PATHLOOKUP_ADVANCED_OPTIONS_PRESET_ID_LOCAL_STORAGE]: string;
  [PATHLOOKUP_HOST_TO_DEFAULT_GW_TYPE_LOCAL_STORAGE]: string;
  [PATHLOOKUP_MULTICAST_TYPE_LOCAL_STORAGE]: string;
  [PATHLOOKUP_UNICAST_TYPE_LOCAL_STORAGE]: string;
}
// biome-ignore lint/correctness/noUndeclaredVariables: global declaration
declare const localStorage: IpfLocalStorage<PathLookupLocalStorage>;

const getParametersFromLocalStorage = <T,>(
  storageKey: keyof PathLookupLocalStorage,
  schema: Joi.Schema,
): T | null => {
  const localStorageValues = localStorage.getItem(storageKey);

  if (localStorageValues === null || localStorageValues === '') {
    return null;
  }

  const parsed = JSON.parse(localStorageValues);
  return makeIsValid<T>(schema)(parsed) ? parsed : null;
};

export const getPathLookupAdvancedOptionsPresetIdFromLocalStorage = ():
  | string
  | null => {
  const localStorageValue = localStorage.getItem(
    PATHLOOKUP_ADVANCED_OPTIONS_PRESET_ID_LOCAL_STORAGE,
  );

  return localStorageValue ?? null;
};

export const getPathLookupUnicastFromLocalStorage =
  (): GraphPathLookupUnicastParameters | null =>
    getParametersFromLocalStorage<GraphPathLookupUnicastParameters>(
      PATHLOOKUP_UNICAST_TYPE_LOCAL_STORAGE,
      pathLookupUnicastParametersSchema,
    );

export const getPathLookupMulticastFromLocalStorage =
  (): GraphPathLookupMulticastParameters | null =>
    getParametersFromLocalStorage<GraphPathLookupMulticastParameters>(
      PATHLOOKUP_MULTICAST_TYPE_LOCAL_STORAGE,
      pathLookupMulticastParametersSchema,
    );

export const getPathLookupHostToDefaultGWFromLocalStorage =
  (): GraphPathLookupHostToDefaultGWParameters | null =>
    getParametersFromLocalStorage<GraphPathLookupHostToDefaultGWParameters>(
      PATHLOOKUP_HOST_TO_DEFAULT_GW_TYPE_LOCAL_STORAGE,
      pathLookupHostToDefaultGWParametersSchema,
    );

const localStorageKeyByPathLookupType: {
  [Key in PathLookupType]: keyof PathLookupLocalStorage;
} = {
  hostToDefaultGW: PATHLOOKUP_HOST_TO_DEFAULT_GW_TYPE_LOCAL_STORAGE,
  multicast: PATHLOOKUP_MULTICAST_TYPE_LOCAL_STORAGE,
  unicast: PATHLOOKUP_UNICAST_TYPE_LOCAL_STORAGE,
};

export const savePathLookupAdvancedSettingsPresetIdToLocalStorage = (
  presetId: string,
): void => {
  localStorage.setItem(
    PATHLOOKUP_ADVANCED_OPTIONS_PRESET_ID_LOCAL_STORAGE,
    presetId,
  );
};

export const savePathLookupParametersToLocalStorage = (
  parameters: GraphPathLookupParameters,
): void => {
  localStorage.setItem(
    localStorageKeyByPathLookupType[parameters.pathLookupType],
    JSON.stringify(parameters),
  );
};
